import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Maximize2 } from "lucide-react";
import { projects } from "../data/projects";
import { FadeUp, MaskedLines } from "../components/Reveal";
import Lightbox from "../components/Lightbox";

function Meta({ project }) {
  const rows = [
    ["Client", project.client],
    ["Year", project.year],
    ["Role", project.role],
    ["Sector", project.sector],
  ];
  return (
    <dl className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4" data-testid="case-meta">
      {rows.map(([k, v]) => (
        <div key={k} className="bg-[#0B0C0E] p-5">
          <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">{k}</dt>
          <dd className="mt-2 text-sm text-[#F9F9FB]">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function NextLink({ next, route }) {
  return (
    <Link
      to={`/${route}/${next.slug}`}
      data-testid="next-project-link"
      className="group block border-t border-white/10 py-16"
    >
      <p className="eyebrow mb-4">Next {route} project</p>
      <span className="flex items-center justify-between gap-4">
        <span className="text-3xl font-light tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:text-5xl">
          {next.title}
        </span>
        <ArrowRight className="shrink-0 text-[#616775] transition-all duration-500 group-hover:translate-x-2 group-hover:text-umber" size={32} />
      </span>
    </Link>
  );
}

function CorporateCase({ project, next }) {
  return (
    <article className="px-6 pt-32 lg:px-12" data-testid="corporate-case-study">
      <FadeUp>
        <Link to="/corporate" data-testid="back-to-route-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0] transition-colors hover:text-white">
          <ArrowLeft size={14} /> Corporate
        </Link>
      </FadeUp>
      <header className="mt-12 max-w-4xl">
        <p className="eyebrow mb-6">{project.client} · {project.year}</p>
        <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
          <MaskedLines lines={[project.title]} delay={0.1} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 text-base font-light leading-relaxed text-[#9EA3B0] sm:text-lg">
            {project.summary}
          </p>
        </FadeUp>
      </header>

      <FadeUp className="mt-14" delay={0.1}>
        <Meta project={project} />
      </FadeUp>

      <FadeUp className="mt-14 overflow-hidden border border-white/10">
        <img
          src={project.heroImage}
          alt={`${project.title}, ${project.client}`}
          data-testid="corporate-hero"
          className="aspect-[21/9] w-full object-cover"
        />
      </FadeUp>

      <section className="mt-14 grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4" aria-label="Key metrics">
        {project.metrics.map((m) => (
          <FadeUp key={m.label} className="bg-[#0B0C0E] p-8">
            <p className="text-3xl font-light tracking-tight text-umber sm:text-4xl">{m.value}</p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#616775]">{m.label}</p>
          </FadeUp>
        ))}
      </section>

      <section className="mt-24" aria-label="Case study chapters">
        {project.chapters.map((ch) => (
          <FadeUp key={ch.num} className="grid gap-6 border-t border-white/10 py-16 md:grid-cols-12">
            <div className="md:col-span-3">
              <span className="font-mono text-5xl font-light text-white/15">{ch.num}</span>
            </div>
            <div className="md:col-span-7">
              <h2 className="text-2xl font-light tracking-tight sm:text-3xl">{ch.title}</h2>
              {ch.body.map((para, i) => (
                <p key={i} className="mt-5 text-sm leading-relaxed text-[#9EA3B0] sm:text-base">
                  {para}
                </p>
              ))}
            </div>
          </FadeUp>
        ))}
      </section>

      <div className="grid gap-6 border-t border-white/10 py-14 md:grid-cols-2">
        {project.images.map((src, i) => (
          <FadeUp key={src} delay={i * 0.1} className="overflow-hidden border border-white/10">
            <img
              src={src}
              alt={`${project.title}, supporting visual ${i + 1}`}
              loading="lazy"
              className="aspect-[16/10] w-full object-cover transition-transform duration-[1.2s] hover:scale-[1.02]"
            />
          </FadeUp>
        ))}
      </div>

      <NextLink next={next} route="corporate" />
    </article>
  );
}

function StoryBlock({ block, onOpen }) {
  if (block.type === "quote") {
    return (
      <FadeUp className="md:col-span-2">
        <blockquote className="mx-auto max-w-3xl py-16 text-center font-serif text-3xl font-light italic leading-snug text-[#F9F9FB] sm:text-4xl">
          “{block.text}”
        </blockquote>
      </FadeUp>
    );
  }
  if (block.type === "image") {
    return (
      <FadeUp className={block.wide ? "md:col-span-2" : ""}>
        <figure className="group overflow-hidden border border-white/10">
          <button type="button" onClick={onOpen} aria-label={`Expand ${block.caption}`} data-testid="story-image-button" className="relative block w-full overflow-hidden text-left">
            <img
              src={block.src}
              alt={block.caption}
              loading="lazy"
              style={block.bg ? { backgroundColor: block.bg } : undefined}
              className={`w-full transition-transform duration-[1.4s] ease-out group-hover:scale-105 ${block.contain ? "object-contain" : "object-cover"} ${block.aspect || (block.wide ? "aspect-[21/9]" : "aspect-[4/3]")}`}
            />
            <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center border border-white/20 bg-[#0B0C0E]/70 text-white/80 opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
              <Maximize2 size={13} />
            </span>
          </button>
          <figcaption className="border-t border-white/10 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">
            {block.caption}
          </figcaption>
        </figure>
      </FadeUp>
    );
  }
  if (block.type === "text") {
    return (
      <FadeUp className="flex flex-col justify-center py-6 md:py-0">
        <h2 className="font-serif text-3xl font-light italic tracking-tight sm:text-4xl">{block.heading}</h2>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-[#9EA3B0] sm:text-base">{block.body}</p>
      </FadeUp>
    );
  }
  if (block.type === "palette") {
    return (
      <FadeUp className="md:col-span-2">
        <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 md:grid-cols-4" data-testid="palette-swatches">
          {block.colors.map((c) => (
            <div key={c.hex} className="bg-[#0B0C0E]">
              <div className="h-24" style={{ backgroundColor: c.hex }} />
              <p className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9EA3B0]">
                {c.name} · {c.hex}
              </p>
            </div>
          ))}
        </div>
      </FadeUp>
    );
  }
  return null;
}

function CreativeCase({ project, next }) {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const [lbIndex, setLbIndex] = useState(null);
  const gallery = [
    { src: project.heroImage, caption: `${project.title}, hero artwork` },
    ...project.story.filter((b) => b.type === "image").map((b) => ({ src: b.src, caption: b.caption })),
  ];
  const close = useCallback(() => setLbIndex(null), []);
  const step = useCallback((d) => setLbIndex((i) => (i + d + gallery.length) % gallery.length), [gallery.length]);
  let imgCount = 0;

  return (
    <article data-testid="creative-case-study">
      <div ref={heroRef} className="relative h-[78svh] overflow-hidden">
        <motion.img
          src={project.heroImage}
          alt={`${project.title}, hero artwork`}
          className="h-full w-full cursor-zoom-in object-cover"
          style={{ y, scale: 1.18, objectPosition: project.focal }}
          onClick={() => setLbIndex(0)}
          data-testid="creative-hero-image"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/20 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 px-6 pb-12 lg:px-12">
          <p className="eyebrow mb-4">{project.client} · {project.year}</p>
          <h1 className="max-w-4xl text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[project.title]} delay={0.15} />
          </h1>
        </div>
      </div>

      <div className="px-6 lg:px-12">
        <FadeUp className="mt-14 max-w-2xl">
          <p className="font-serif text-2xl font-light italic leading-snug text-[#F9F9FB] sm:text-3xl">
            {project.summary}
          </p>
        </FadeUp>
        <FadeUp className="mt-12" delay={0.1}>
          <Meta project={project} />
        </FadeUp>

        <section className="mt-20 grid gap-6 md:grid-cols-2" aria-label="Visual story">
          {project.story.map((block, i) => {
            const idx = block.type === "image" ? ++imgCount : null;
            return <StoryBlock key={i} block={block} onOpen={idx ? () => setLbIndex(idx) : undefined} />;
          })}
        </section>

        <NextLink next={next} route="creative" />
      </div>
      <Lightbox items={gallery} index={lbIndex} onClose={close} onStep={step} />
    </article>
  );
}

export default function CaseStudy({ route }) {
  const { slug } = useParams();
  const list = projects.filter((p) => p.route === route);
  const project = list.find((p) => p.slug === slug);

  useEffect(() => {
    if (project) document.title = `${project.title} · Haus Ov Harii`;
  }, [project]);

  if (!project) return <Navigate to={`/${route}`} replace />;
  const next = list[(list.indexOf(project) + 1) % list.length];

  return route === "corporate" ? (
    <CorporateCase project={project} next={next} />
  ) : (
    <CreativeCase project={project} next={next} />
  );
}
